import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { mapLanguages } from 'app/utils/translations';
import { useCss } from 'entities/style';
import {
  documentKeys,
  useCreateTemplateStyle,
  useEditorDocumentQuery,
  useEditorSection,
  SectionEntry,
  EditorDocument,
  ReferenceDataValue,
} from 'entities/document';
import { useReferenceList } from 'entities/reference-data';
import type { EditorScope } from 'app/state/contexts/EditorScopeContext';
import type { Descendant } from 'slate';

type TemplateInput = { style?: unknown; superId?: number | string; revisionId?: string };

export const useDocument = (
  documentId: number | string,
  documentRevisionId?: string,
  sectionId?: string,
  sectionRevisionId?: string,
  translationId?: string,
  showErrors = true,
  useDraft = false,
  usingStaticStructure?: Descendant[],
  withStyle?: unknown,
  useReferenceData?: boolean,
  embedd = false
) => {
  const queryClient = useQueryClient();

  const { data: css } = useCss({
    documentSuperId: documentId,
    documentRevisionId,
    useDraft: useDraft,
    withStyle: withStyle as string | false | undefined,
    embedd,
  });
  const { mutate: createTemplateStyle } = useCreateTemplateStyle(documentId);

  const { data: fetchedDocument } = useEditorDocumentQuery({ documentId, documentRevisionId, useDraft: useDraft, withStyle, showErrors });
  const document = fetchedDocument ?? null;

  const inputSignature = `${documentId}|${documentRevisionId}|${sectionId}|${sectionRevisionId}|${translationId}`;
  const [sectionTarget, setSectionTarget] = useState<{ signature: string; revisionId?: string } | null>(null);

  useEffect(() => {
    if (!fetchedDocument || !sectionId) return;
    if (sectionTarget?.signature === inputSignature) return;
    const langs = mapLanguages(fetchedDocument, sectionId);
    const revisionId = translationId
      ? langs.find((lang) => lang.superId === translationId)?.revisionId
      : sectionRevisionId || fetchedDocument.content.sections.find((s: SectionEntry) => s.superId === sectionId)?.revisionId;
    setSectionTarget({ signature: inputSignature, revisionId });
  }, [fetchedDocument, sectionId, sectionRevisionId, translationId, inputSignature, sectionTarget]);

  const { data: fetchedSection } = useEditorSection(sectionTarget?.signature === inputSignature ? sectionTarget.revisionId : undefined, showErrors);

  const staticSection = useMemo(() => (usingStaticStructure ? { content: usingStaticStructure } : null), [usingStaticStructure]);
  const section = staticSection ?? (fetchedSection as SectionEntry | undefined) ?? null;

  const { data: referenceList } = useReferenceList(useReferenceData ? fetchedDocument : undefined);
  const referenceData: ReferenceDataValue[] = referenceList ?? [];

  const addTemplateToDocument = (template: TemplateInput) => {
    if (document?.content.design?.find((style) => style.templateSuperId === template.superId)) {
      return;
    }

    createTemplateStyle(
      { template },
      {
        onSuccess: (data: EditorDocument) => {
          queryClient.setQueryData(documentKeys.editorDocument(documentId, documentRevisionId, useDraft, withStyle), data);
        },
      }
    );
  };

  const scope: EditorScope = useMemo(
    () => ({ documentId, documentRevisionId, sectionId, sectionRevisionId, translationId, useDraft: useDraft, withStyle, embedd }),
    [documentId, documentRevisionId, sectionId, sectionRevisionId, translationId, useDraft, withStyle, embedd]
  );

  return { document, section, css, referenceData, addTemplateToDocument, scope };
};
